const benefits = [
  {
    icon: 'ri-funds-line',
    title: 'Avoid Overpaying',
    body: 'We identify overpriced bids and help negotiate better pricing — before you sign anything.',
  },
  {
    icon: 'ri-checkbox-multiple-line',
    title: 'Make Confident Decisions',
    body: 'Clear, side-by-side comparisons of quality, scope, and warranties so the right choice is obvious.',
  },
  {
    icon: 'ri-time-line',
    title: 'Save Time & Reduce Stress',
    body: 'We handle contractor coordination, analysis, and communication — so you don\u2019t have to.',
  },
  {
    icon: 'ri-links-line',
    title: 'Gain Real Leverage',
    body: 'Contractors respond faster and more seriously because of our ongoing relationships in the market.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Stay Fully Protected',
    body: 'All contractors are vetted for active licensing, insurance, bonding, and complaint history.',
  },
];

export default function BenefitsSection() {
  return (
    <section id="benefits" className="bg-white py-24 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-sm font-semibold tracking-widest text-emerald-600 uppercase mb-3">
            What You Actually Get
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight">
            with OverUnder Roofing
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            This isn&apos;t just convenience — it&apos;s protection, clarity, and better outcomes.
          </p>
          <div className="w-20 h-1 bg-emerald-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Benefits grid — 3 top, 2 centered bottom */}
        <div className="flex flex-col gap-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.slice(0, 3).map((b, i) => (
              <BenefitCard key={i} benefit={b} index={i} />
            ))}
          </div>
          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:w-2/3 md:mx-auto">
            {benefits.slice(3).map((b, i) => (
              <BenefitCard key={i + 3} benefit={b} index={i + 3} />
            ))}
          </div>
        </div>

        {/* Closing callout */}
        <div className="mt-14 bg-gradient-to-r from-emerald-700 to-emerald-600 rounded-2xl px-8 py-7 text-center max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-seedling-line text-2xl text-emerald-200"></i>
            </div>
            <p className="text-white text-lg font-medium leading-relaxed">
              Instead of guessing or relying on sales pitches, you get{' '}
              <span className="text-emerald-200 font-semibold">clarity</span>,{' '}
              <span className="text-emerald-200 font-semibold">confidence</span>, and{' '}
              <span className="text-emerald-200 font-semibold">peace of mind</span>.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}

interface Benefit {
  icon: string;
  title: string;
  body: string;
}

function BenefitCard({ benefit, index }: { benefit: Benefit; index: number }) {
  return (
    <div className="bg-slate-50 rounded-2xl border border-slate-100 p-7 flex flex-col gap-4 hover:border-emerald-200 hover:bg-emerald-50/40 transition-colors cursor-default">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-100 shrink-0">
          <i className={`${benefit.icon} text-2xl text-emerald-600`}></i>
        </div>
        <span className="text-4xl font-black text-emerald-100 leading-none select-none">
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{benefit.body}</p>
      </div>
    </div>
  );
}
